/**
 * Maintenance Requirements - Multi-Country Wrapper
 * This file provides backwards compatibility while loading country-specific regulations
 * Original UK implementation is preserved in maintenanceRequirements-original.js
 */

import { getCurrentCountry } from './regulations/regulationLoader';
// Import UK functions for synchronous backward compatibility
import * as ukRegulations from './maintenanceRequirements-original.js';

// Lazy load the appropriate country module
let regulationsModule = null;
let currentCountry = null;

async function getRegulations() {
  const country = getCurrentCountry();
  
  // Reload if country changed
  if (!regulationsModule || country !== currentCountry) {
    currentCountry = country;
    
    // For now, we only have UK, but this structure supports future countries
    if (country === 'UK') {
      // Use the original file that we know works
      regulationsModule = await import('./maintenanceRequirements-original.js');
    } else {
      // Future: load other country modules
      const countryModule = await import(`./regulations/${country}/maintenanceRequirements.js`);
      regulationsModule = countryModule;
    }
  }
  
  return regulationsModule;
}

// Export the constants immediately (using UK defaults)
export const MAINTENANCE_CATEGORIES = {
  SURVEYS: 'surveys',
  LSA: 'lsa',
  FFE: 'ffe',
  RADIO: 'radio',
  MACHINERY: 'machinery',
  ELECTRICAL: 'electrical',
  NAVIGATION: 'navigation',
  MEDICAL: 'medical',
  SPECIAL_OPS: 'special_ops',
  CREW: 'crew'
};

// Export async functions that load the right country module
export async function getMaintenanceScheduleAsync(vessel) {
  const regs = await getRegulations();
  return regs.getMaintenanceSchedule(vessel);
}

// Export synchronous version for backward compatibility
// This works because we're currently only using UK regulations
export function getMaintenanceSchedule(vessel) {
  return ukRegulations.getMaintenanceSchedule(vessel);
}

export async function calculateMaintenanceDueAsync(vessel, lastMaintenance) {
  const regs = await getRegulations();
  return regs.calculateMaintenanceDue(vessel, lastMaintenance);
}

// Export synchronous version for backward compatibility
export function calculateMaintenanceDue(vessel, lastMaintenance) {
  return ukRegulations.calculateMaintenanceDue(vessel, lastMaintenance);
}

// Re-export all UK functions as defaults for now
export const {
  getMaintenanceSchedule: getMaintenanceScheduleSync, // Export synchronous version for backward compatibility
  getLSAMaintenance,
  getFFEMaintenance,
  getMachineryMaintenance,
  getElectricalMaintenance,
  getNavigationMaintenance,
  getRadioMaintenance,
  getMedicalMaintenance,
  getSpecialOpsMaintenance,
  getInlandSurveyRequirements,
  getInlandFFEMaintenance,
  getInlandMachineryMaintenance,
  getInlandElectricalMaintenance,
  getInlandNavigationMaintenance,
  getInlandMedicalRequirements,
  getInlandSpecialOpsMaintenance,
  getAINAHireBoatCodeRequirements
} = ukRegulations;

// Default export for backward compatibility
const maintenanceRequirements = {
  MAINTENANCE_CATEGORIES,
  getMaintenanceSchedule,
  getMaintenanceScheduleAsync,
  calculateMaintenanceDue,
  calculateMaintenanceDueAsync,
  getLSAMaintenance,
  getFFEMaintenance,
  getMachineryMaintenance,
  getElectricalMaintenance,
  getNavigationMaintenance,
  getRadioMaintenance,
  getMedicalMaintenance,
  getSpecialOpsMaintenance
};

export default maintenanceRequirements;